import React, { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { TaskAttentionIcon, CheckCircleFilledIcon } from '@/src/renderer/components/icons'
import type { AttentionLevel } from '@/src/renderer/lib/db/models'
import { ShortcutHint } from '@/src/renderer/components/ui/ShortcutHint'
import { Button } from '@/src/renderer/components/ui/Button'
import type { AttentionDropdownContentProps } from './AttentionDropdown.types'
import './AttentionDropdown.scss'

const ATTENTION_LEVELS: { value: AttentionLevel; labelKey: string; shortcut: string; macro: string }[] = [
  { value: 'high', labelKey: 'taskCreation.attentionHigh', shortcut: '1', macro: '!1' },
  { value: 'medium', labelKey: 'taskCreation.attentionMedium', shortcut: '2', macro: '!2' },
  { value: 'low', labelKey: 'taskCreation.attentionLow', shortcut: '3', macro: '!3' },
]

/**
 * Content for the attention dropdown menu.
 * Renders inside a DropdownMenuContent managed by TaskActionButton.
 *
 * Features:
 * - Search/filter input at the top
 * - Macro input (!1, !2, !3) for quick selection
 * - Keyboard shortcuts (1, 2, 3) when dropdown is open
 */
export const AttentionDropdownContent: React.FC<AttentionDropdownContentProps> = ({ value, onValueChange, onClose, onRemove, placement = 'actionbar' }) => {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const inline = placement === 'inline'

  const filteredLevels = inputValue
    ? ATTENTION_LEVELS.filter(level =>
        t(level.labelKey).toLowerCase().includes(inputValue.toLowerCase())
      )
    : ATTENTION_LEVELS

  const handleSelect = useCallback((attention: AttentionLevel) => {
    onValueChange(attention)
    onClose()
  }, [onValueChange, onClose])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInputValue(val)

    // Check for macro input (!1, !2, !3)
    const macroMatch = ATTENTION_LEVELS.find(level => val.trim() === level.macro)
    if (macroMatch) {
      handleSelect(macroMatch.value)
    }
  }, [handleSelect])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Number keys 1-3 select attention level directly
    const level = ATTENTION_LEVELS.find(l => l.shortcut === e.key)
    if (level) {
      e.preventDefault()
      e.stopPropagation()
      handleSelect(level.value)
    }
  }, [handleSelect])

  return (
    <div onKeyDown={handleKeyDown} data-placement={placement}>
      <div className="attention-dropdown__search-wrapper">
        <div className="attention-dropdown__search">
          <TaskAttentionIcon size={16} />
          <input
            ref={inputRef}
            className="attention-dropdown__input"
            placeholder={t('taskCreation.attentionPlaceholder')}
            value={inputValue}
            onChange={handleInputChange}
            // In den Aufgaben-Details stehen sieben Inhalte gleichzeitig offen — sechs
            // von ihnen hätten ein `autoFocus`, und der letzte gewänne. Der Fokus gehört
            // dort der Liste (CONTEXT.md, Aufgaben-Details), bis jemand hineingreift.
            autoFocus={!inline}
          />
        </div>
      </div>
      {filteredLevels.map(level => (
        <button
          key={level.value}
          type="button"
          className={`context-menu-content__item attention-dropdown__item${level.value === value ? ' attention-dropdown__item--passive-selected' : ''}`}
          onClick={() => handleSelect(level.value)}
        >
          <TaskAttentionIcon level={level.value} size={14} className="context-menu-content__item-icon" />
          {t(level.labelKey)}
          <div className="attention-dropdown__item-right">
            {level.value === value && (
              <CheckCircleFilledIcon size={16} className="attention-dropdown__check" />
            )}
            <ShortcutHint>{level.shortcut}</ShortcutHint>
          </div>
        </button>
      ))}
      {/* Der Griff entscheidet, nicht der Einbauort — wie am Wann-Kasten (#216). Im Menü reicht ihn
          `TaskMenuTargetContent`, sobald eine Aufmerksamkeit dasteht (Sven, 10.10.2026). */}
      {!inline && onRemove && (
        <div className="attention-dropdown__remove-section">
          <div className="context-menu-content__separator" />
          <div className="attention-dropdown__remove-wrapper">
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>
              {t('taskCreation.attentionRemove')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
